import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, IndianRupee, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

// Eligibility Calculator Constants
const MIN_MONTHLY_INCOME = 25000;
const MAX_MONTHLY_INCOME = 1000000;
const INCOME_STEP = 5000;
const MIN_AGE = 21;
const MAX_AGE = 65;
const MIN_TENURE_YEARS = 1;
const MAX_TENURE_YEARS = 30;

export const EligibilityCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [existingEMI, setExistingEMI] = useState(0);
  const [age, setAge] = useState(30);
  const [tenureYears, setTenureYears] = useState(20);
  const [interestRate, setInterestRate] = useState(9.5);
  const [employmentType, setEmploymentType] = useState('salaried');
  
  const [eligibility, setEligibility] = useState({
    loanAmount: 0,
    monthlyEMI: 0,
    maxTenure: 0
  });

  const calculateEligibility = useCallback(() => {
    // FOIR (Fixed Obligation to Income Ratio) - typically 50-60%
    const foirPercent = employmentType === 'salaried' ? 0.55 : 0.50;
    
    // Available income for EMI
    const availableForEMI = (monthlyIncome * foirPercent) - existingEMI;
    
    if (availableForEMI <= 0) {
      setEligibility({
        loanAmount: 0,
        monthlyEMI: 0,
        maxTenure: 0
      });
      return;
    }
    
    // Calculate maximum loan based on EMI capacity
    const ratePerMonth = interestRate / 12 / 100;
    const tenureMonths = tenureYears * 12;
    
    // Loan amount formula: EMI * [(1 + r)^n - 1] / [r * (1 + r)^n]
    const loanAmount = availableForEMI * 
      ((Math.pow(1 + ratePerMonth, tenureMonths) - 1) / 
       (ratePerMonth * Math.pow(1 + ratePerMonth, tenureMonths)));
    
    // Max tenure based on age (retirement age 60-65)
    const maxRetirementAge = 65;
    const maxTenureByAge = (maxRetirementAge - age);
    
    setEligibility({
      loanAmount: loanAmount,
      monthlyEMI: availableForEMI,
      maxTenure: Math.min(tenureYears, maxTenureByAge)
    });
  }, [monthlyIncome, existingEMI, age, tenureYears, interestRate, employmentType]);

  useEffect(() => {
    calculateEligibility();
  }, [calculateEligibility]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section id="eligibility-calculator" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Loan Eligibility <span className="text-emerald-600">Calculator</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Check how much loan you're eligible for based on your income and profile
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Input Section */}
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Your Profile
              </CardTitle>
              <CardDescription>Enter your financial details to check eligibility</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Employment Type */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-slate-700">Employment Type</Label>
                <div className="flex gap-3">
                  <Button
                    variant={employmentType === 'salaried' ? 'default' : 'outline'}
                    onClick={() => setEmploymentType('salaried')}
                    className={employmentType === 'salaried' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Salaried
                  </Button>
                  <Button
                    variant={employmentType === 'self-employed' ? 'default' : 'outline'}
                    onClick={() => setEmploymentType('self-employed')}
                    className={employmentType === 'self-employed' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  >
                    Self-Employed
                  </Button>
                </div>
              </div>

              {/* Monthly Income */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Monthly Income</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(monthlyIncome)}</span>
                </div>
                <Slider
                  value={[monthlyIncome]}
                  onValueChange={(value) => setMonthlyIncome(value[0])}
                  min={MIN_MONTHLY_INCOME}
                  max={MAX_MONTHLY_INCOME}
                  step={INCOME_STEP}
                  className="w-full"
                />
              </div>

              {/* Existing EMI */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Existing Monthly EMI</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(existingEMI)}</span>
                </div>
                <Slider
                  value={[existingEMI]}
                  onValueChange={(value) => setExistingEMI(value[0])}
                  min={0}
                  max={monthlyIncome * 0.6}
                  step={1000}
                  className="w-full"
                />
              </div>

              {/* Age */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Age</Label>
                  <span className="text-xl font-bold text-emerald-600">{age} years</span>
                </div>
                <Slider
                  value={[age]}
                  onValueChange={(value) => setAge(value[0])}
                  min={MIN_AGE}
                  max={MAX_AGE}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Tenure */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Loan Tenure</Label>
                  <span className="text-xl font-bold text-emerald-600">{tenureYears} years</span>
                </div>
                <Slider
                  value={[tenureYears]}
                  onValueChange={(value) => setTenureYears(value[0])}
                  min={MIN_TENURE_YEARS}
                  max={MAX_TENURE_YEARS}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Interest Rate */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Interest Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{interestRate.toFixed(1)}%</span>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(value) => setInterestRate(value[0])}
                  min={6}
                  max={20}
                  step={0.1}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Eligibility Result */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <TrendingUp className="w-6 h-6" />
                  Your Loan Eligibility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-6 bg-white rounded-xl border-2 border-emerald-200">
                  <p className="text-sm text-slate-600 mb-2">Maximum Loan Amount</p>
                  <p className="text-4xl font-bold text-emerald-600">
                    {formatCurrency(eligibility.loanAmount)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Monthly EMI</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(eligibility.monthlyEMI)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Max Tenure</p>
                    <p className="text-xl font-bold text-slate-900">{eligibility.maxTenure} years</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Info Cards */}
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 mb-2" />
                <p className="text-sm opacity-90 mb-1">FOIR Applied</p>
                <p className="text-2xl font-bold">{employmentType === 'salaried' ? '55%' : '50%'}</p>
                <p className="text-xs opacity-75 mt-2">Fixed Obligation to Income Ratio</p>
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="bg-emerald-600 text-white border-0">
              <CardContent className="pt-6 text-center">
                <h3 className="text-xl font-bold mb-2">Ready to apply?</h3>
                <p className="mb-4 opacity-90">Get personalized financing solutions</p>
                <Button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="bg-white text-emerald-600 hover:bg-slate-100"
                >
                  Apply Now
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};